<script setup lang="ts">
import { computed, ref } from 'vue';
import type { NyxQuestion, NyxAnswer } from '@atum-remote/nyx-link-protocol';
import AppIcon from './AppIcon.vue';

/**
 * A question the client asked the person. Choices stay local until the person
 * sends them; once the request is answered or expires the card becomes a record.
 */
const props = defineProps<{
  questions: NyxQuestion[];
  pending: boolean;
  answered: boolean;
  canDecide: boolean;
  disabled: boolean;
}>();
const emit = defineEmits<{ answer: [NyxAnswer[]] }>();
const selected = ref<Record<string, string[]>>({});
const complete = computed(() =>
  props.questions.every((q) => (selected.value[q.id]?.length ?? 0) > 0),
);
const open = computed(() => props.pending && !props.answered);
function choose(q: NyxQuestion, id: string, checked: boolean) {
  selected.value[q.id] = q.allowMultiple
    ? checked
      ? [...(selected.value[q.id] ?? []), id]
      : (selected.value[q.id] ?? []).filter((v) => v !== id)
    : [id];
}
</script>

<template>
  <form
    class="nyx-card"
    data-kind="question"
    :data-pending="open ? 'true' : 'false'"
    @submit.prevent="
      open &&
      canDecide &&
      !disabled &&
      complete &&
      emit(
        'answer',
        questions.map((q) => ({ questionId: q.id, selectedOptionIds: selected[q.id] ?? [] })),
      )
    "
  >
    <header class="nyx-card-head">
      <span class="nyx-card-icon" aria-hidden="true"><AppIcon name="question" /></span>
      <strong>{{ questions.length === 1 ? 'Question for you' : 'Questions for you' }}</strong>
      <span v-if="answered" class="tag" data-tone="ok">Answered</span>
      <span v-else-if="!pending" class="tag">No longer pending</span>
      <span v-else class="tag" data-tone="warn">Waiting</span>
    </header>
    <fieldset
      v-for="q in questions"
      :key="q.id"
      class="nyx-question"
      :disabled="disabled || !open || !canDecide"
    >
      <legend>{{ q.prompt }}</legend>
      <label v-for="option in q.options" :key="option.id" class="nyx-option">
        <input
          :name="q.id"
          :type="q.allowMultiple ? 'checkbox' : 'radio'"
          :checked="selected[q.id]?.includes(option.id) ?? false"
          @change="choose(q, option.id, ($event.target as HTMLInputElement).checked)"
        />
        <span>{{ option.label }}</span>
      </label>
      <span v-if="q.allowMultiple" class="field-help">Choose one or more.</span>
    </fieldset>
    <p v-if="open && !canDecide" class="field-help">
      Decide in Nyx Link on the host. This view has read access.
    </p>
    <footer v-else-if="open" class="nyx-card-actions">
      <button type="submit" class="primary small" :disabled="disabled || !complete">
        <AppIcon name="check" />Send answers
      </button>
    </footer>
  </form>
</template>
